import { useState, useEffect, useRef } from 'react';

import {
  findScrollContainer,
  findHeaderElement,
  calculateScrollPosition,
} from '../PhotoDamageAnalyzer/utils';

export interface MessagePair {
  user: any;
  assistant: any | null;
  userIndex: number;
  assistantIndex: number | null;
}

interface UseScrollManagementParams {
  scrollRootRef: React.RefObject<HTMLDivElement | null>;
  messageRefs: React.RefObject<Record<number, HTMLDivElement | null>>;
  pairs: MessagePair[];
  messagesLength: number;
  loadingMessage: string;
  featureTitle: string;
}

interface UseScrollManagementReturn {
  hasUserScrolled: boolean;
  smoothScrollRef: React.MutableRefObject<{
    requestAnimationFrameId: number | null;
    target: number | null;
  }>;
}

/**
 * Custom hook to manage scroll behavior
 * Handles auto-scroll to last message and tracks user scroll state
 */
export const useScrollManagement = ({
  scrollRootRef,
  messageRefs,
  pairs,
  messagesLength,
  loadingMessage,
  featureTitle,
}: UseScrollManagementParams): UseScrollManagementReturn => {
  const [hasUserScrolled, setHasUserScrolled] = useState(false);

  const smoothScrollRef = useRef<{
    requestAnimationFrameId: number | null;
    target: number | null;
  }>({
    requestAnimationFrameId: null,
    target: null,
  });

  // Scroll to last user message below header
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root || !messageRefs.current) return;

    const scrollContainer = findScrollContainer(root);
    if (!scrollContainer) return;

    const headerElement = findHeaderElement(featureTitle, scrollContainer);
    const lastPair = pairs.length > 0 ? pairs[pairs.length - 1] : null;
    const lastUserElement = lastPair
      ? messageRefs.current[lastPair.userIndex]
      : null;

    const scrollPosition = calculateScrollPosition(
      scrollContainer,
      headerElement,
      lastUserElement,
    );

    scrollContainer.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    // Note: scrollRootRef and messageRefs are NOT in dependencies because they're
    // stable ref objects that don't change between renders. The effect re-runs when
    // messagesLength, loadingMessage, featureTitle, or pairs change, and at that
    // time it reads the current values of the refs.
  }, [messagesLength, loadingMessage, featureTitle, pairs]);

  // Track user scroll and cancel smooth scroll if user interacts
  useEffect(() => {
    const root = scrollRootRef.current;
    if (!root) return;
    const scrollContainer = findScrollContainer(root) ?? root;

    const cancelIfAnimating = () => {
      const smoothScrollState = smoothScrollRef.current;
      if (smoothScrollState.requestAnimationFrameId) {
        cancelAnimationFrame(smoothScrollState.requestAnimationFrameId);
        smoothScrollState.requestAnimationFrameId = null;
        smoothScrollState.target = null;
      }
    };

    let scrollTimeout: NodeJS.Timeout;
    const handleUserScroll = () => {
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        const { scrollTop } = scrollContainer;
        const shouldShowSmallFont = scrollTop > 30;

        setHasUserScrolled((prev) => {
          if (prev !== shouldShowSmallFont) {
            return shouldShowSmallFont;
          }
          return prev;
        });
      }, 10);
    };

    scrollContainer.addEventListener('wheel', cancelIfAnimating, {
      passive: true,
    });
    scrollContainer.addEventListener('touchstart', cancelIfAnimating, {
      passive: true,
    });
    scrollContainer.addEventListener('scroll', handleUserScroll, {
      passive: true,
    });

    return () => {
      clearTimeout(scrollTimeout);
      scrollContainer.removeEventListener('wheel', cancelIfAnimating);
      scrollContainer.removeEventListener('touchstart', cancelIfAnimating);
      scrollContainer.removeEventListener('scroll', handleUserScroll);
    };
    // Note: scrollRootRef is NOT in dependencies because it's a stable ref object.
    // This effect runs once on mount to set up event listeners. The element should
    // be available by the time this effect runs since the ref is attached to the DOM.
  }, []);

  return {
    hasUserScrolled,
    smoothScrollRef,
  };
};
